import { errorEvents } from "@/lib/error-events";
import { Dispatch, SetStateAction } from "react";
import { apiUrl } from "./apiConfig";

export interface IChatMessage {
    role: 'user' | 'assistant';
    content: string;
}

export async function streamChat(
    prompt: string,
    threadId: string | null,
    onChunk: (chunk: string) => void,
    onDone: () => void,
    onError: (error: Error) => void,
    currentTokens: number,
    setTokenConsumption: Dispatch<SetStateAction<number>>,
    onThreadId: (id: string) => void
): Promise<void> {
    try {
        console.log('streamChat prompt:', prompt, 'threadId:', threadId);
        const token = localStorage.getItem('token');
        const response = await fetch(apiUrl('/aboutHenrik/assistantTrainedData'), {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}`
            },
            body: JSON.stringify({ prompt, threadId })
        });

        if (!response.ok) {
            const errorData = await response.json().catch(() => ({}));
            throw new Error(errorData.message || `Chat request failed: ${response.status}`);
        }

        if (!response.body) {
            throw new Error('No response body');
        }

        const reader = response.body.getReader();
        const decoder = new TextDecoder();
        let buffer = '';

        while (true) {
            const { done, value } = await reader.read();
            if (done) break;
            buffer += decoder.decode(value, { stream: true });

            // Process SSE lines
            let newlineIndex: number;
            while ((newlineIndex = buffer.indexOf('\n')) !== -1) {
                let line = buffer.slice(0, newlineIndex);
                buffer = buffer.slice(newlineIndex + 1);

                if (line.endsWith('\r')) line = line.slice(0, -1);
                if (line.startsWith(':') || line.trim() === '') continue;
                if (!line.startsWith('data: ')) continue;

                const jsonStr = line.slice(6).trim();
                if (jsonStr === '[DONE]') {
                    onDone();
                    return;
                }

                try { // updating the messages
                    const parsed = JSON.parse(jsonStr);
                    const content = parsed.content as string | undefined;
                    if (content) {
                        onChunk(content);
                    }

                    if (parsed.threadId) {
                        onThreadId(parsed.threadId);
                    }

                    if (parsed.usage) {
                        // Handle token usage (e.g., log it or show to user)
                        console.log("Token Consumption:", parsed.usage);
                        setTokenConsumption(currentTokens + parsed.usage.total_tokens);
                        // parsed.usage looks like:
                        // { prompt_tokens: 50, completion_tokens: 100, total_tokens: 150 }
                    }
                } catch {
                    // Incomplete JSON, put back and wait for more
                    buffer = line + '\n' + buffer;
                    break;
                }
            }
        }
        // Final flush
        if (buffer.trim()) {
            for (let raw of buffer.split('\n')) {
                if (!raw) continue;
                if (raw.endsWith('\r')) raw = raw.slice(0, -1);
                if (raw.startsWith(':') || raw.trim() === '') continue;
                if (!raw.startsWith('data: ')) continue;
                const jsonStr = raw.slice(6).trim();
                if (jsonStr === '[DONE]') continue;
                try {
                    const parsed = JSON.parse(jsonStr);
                    const content = parsed.choices?.[0]?.delta?.content as string | undefined;
                    if (content) onChunk(content);
                } catch { /* ignore */ }
            }
        }

        onDone();
    } catch (error) {
        onError(error instanceof Error ? error : new Error('Unknown error'));
        const message = error.message || "An unexpected error occurred connecting to the server";
        errorEvents.emit(message, 'AI assistant error');
    }
}
